import { useState } from "react";
import { useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Trans, useLingui } from "@lingui/react/macro";
import * as Sentry from "@sentry/react";
import { z } from "zod";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { ExpansionPanel } from "@medoma/components/ExpansionPanel/ExpansionPanel";
import { Heading } from "@medoma/components/Heading/Heading";
import { Text } from "@medoma/components/Text/Text";
import { activityRequirementStatusSchema } from "@medoma/models/activities";
import type { IExistingPatient } from "@medoma/models/patients";
import type {
  IEmployeeCompetence,
  IMedicalCompetence,
} from "@medoma/models/shifts";
import { isBatchTemplate } from "@medoma/models/templates";
import { deducedError } from "@/Utils/ErrorUtils";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import { CompetenceChip } from "@/components/Chips/CompetenceChip";
import {
  templatesQueryOptions,
  tagsByGroupQueryOptions,
  type IActivityTemplate,
  type IActivityTemplateWithPatient,
  type IBatchTemplate,
  type ITemplate,
} from "@/api/Templates";
import { usePatient } from "@/api/Patients";
import { CreateActivityFromTemplateForm } from "./CreateActivityFromTemplateForm/CreateActivityFromTemplateForm";
import { CreateActivitiesFromBatchTemplateForm } from "./CreateActivitiesFromBatchTemplateForm/CreateActivitiesFromBatchTemplateForm";
import { groupTemplatesByTag } from "./tagUtils";
import {
  partitionTemplates,
  sortActivityTemplates,
  sortBatchTemplates,
} from "./templateUtils";
import styles from "./TemplatesList.module.scss";

const ActivityTemplateItem = ({
  template,
  onSuccess,
  currentEmployeeCompetence,
}: {
  template: IActivityTemplate;
  onSuccess: () => void;
  currentEmployeeCompetence: IEmployeeCompetence;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { title, category, requiredCompetences } = template.template;

  return (
    <ExpansionPanel
      triggerAriaLabel={title}
      isExpanded={isExpanded}
      setIsExpanded={setIsExpanded}
      trigger={
        <div className={styles.templateHeader}>
          <CategoryIcon category={category} size="small" />
          <Text element="span">{title}</Text>
          <div className={styles.competences}>
            {(requiredCompetences ?? []).map((competence) => (
              <CompetenceChip
                key={competence}
                competence={competence}
                state={activityRequirementStatusSchema.enum.neutral}
              />
            ))}
          </div>
        </div>
      }
    >
      <div className={styles.content}>
        <CreateActivityFromTemplateForm
          template={template}
          onSuccess={onSuccess}
          currentEmployeeCompetence={currentEmployeeCompetence}
        />
      </div>
    </ExpansionPanel>
  );
};

const BatchTemplateItem = ({
  template,
  onSuccess,
}: {
  template: IBatchTemplate;
  onSuccess: () => void;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { title, activities } = template.template;

  const combinedCompetences = Array.from(
    new Set<IMedicalCompetence>(
      activities.flatMap((activity) => activity.requiredCompetences ?? []),
    ),
  );

  return (
    <ExpansionPanel
      triggerAriaLabel={title}
      isExpanded={isExpanded}
      setIsExpanded={setIsExpanded}
      trigger={
        <div className={styles.templateHeader}>
          <CategoryIcon category="MultipleCategories" size="small" />
          <Text element="span">{title}</Text>
          <div className={styles.competences}>
            {combinedCompetences.map((competence) => (
              <CompetenceChip
                key={competence}
                competence={competence}
                state={activityRequirementStatusSchema.enum.neutral}
              />
            ))}
          </div>
        </div>
      }
    >
      <div className={styles.content}>
        <CreateActivitiesFromBatchTemplateForm
          template={template}
          onSuccess={onSuccess}
        />
      </div>
    </ExpansionPanel>
  );
};

const TemplateGroup = ({
  templates,
  onSuccess,
  currentEmployeeCompetence,
}: {
  templates: ITemplate[];
  onSuccess: () => void;
  currentEmployeeCompetence: IEmployeeCompetence;
}) => {
  const { batches, activities } = partitionTemplates(templates);
  const sortedBatches = sortBatchTemplates(batches);
  const sortedActivities = sortActivityTemplates(activities);

  return (
    <ul className={styles.list}>
      {sortedBatches.map((template) => (
        <li key={template.templateId}>
          <BatchTemplateItem template={template} onSuccess={onSuccess} />
        </li>
      ))}
      {sortedActivities.map((template) => (
        <li key={template.templateId}>
          <ActivityTemplateItem
            template={template}
            onSuccess={onSuccess}
            currentEmployeeCompetence={currentEmployeeCompetence}
          />
        </li>
      ))}
    </ul>
  );
};

export const TemplatesList = ({
  onSuccess,
  currentEmployeeCompetence,
}: {
  onSuccess: () => void;
  currentEmployeeCompetence: IEmployeeCompetence;
}) => {
  const { patientId } = z.object({ patientId: z.string() }).parse(useParams());
  const { t } = useLingui();
  const {
    data: patient,
    isPending: isPatientPending,
    isError: isPatientError,
    error: patientError,
  } = usePatient(patientId);

  if (isPatientPending) {
    return <Loading message={t`Hämtar patient`} />;
  }

  if (isPatientError) {
    Sentry.captureException(patientError);
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta patientdetaljer.`} ${deducedError(patientError)}`}
      />
    );
  }

  return (
    <PatientTemplatesList
      patient={patient}
      onSuccess={onSuccess}
      currentEmployeeCompetence={currentEmployeeCompetence}
    />
  );
};

const PatientTemplatesList = ({
  patient,
  onSuccess,
  currentEmployeeCompetence,
}: {
  patient: IExistingPatient;
  onSuccess: () => void;
  currentEmployeeCompetence: IEmployeeCompetence;
}) => {
  const { t } = useLingui();
  const siteId = patient.site.id;
  const {
    data: templates,
    isPending: isTemplatesPending,
    isError: isTemplatesError,
    error: templatesError,
  } = useQuery(templatesQueryOptions(siteId));
  const {
    data: groupTags,
    isPending: isGroupTagsPending,
    isError: isGroupTagsError,
    error: groupTagsError,
  } = useQuery(tagsByGroupQueryOptions(siteId, "group"));

  if (isTemplatesPending) {
    return <Loading message={t`Hämtar mallar`} />;
  }

  if (isGroupTagsPending) {
    return <Loading message={t`Hämtar mallgrupper`} />;
  }

  if (isTemplatesError) {
    Sentry.captureException(templatesError);
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta mallar`} ${deducedError(templatesError)}`}
      />
    );
  }

  if (isGroupTagsError) {
    Sentry.captureException(groupTagsError);
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta mallar`} ${deducedError(groupTagsError)}`}
      />
    );
  }

  // `withoutPatient` templates aren't relevant on a patient's page, so we
  // build the list from `withPatient` activities + batches only. Both share a
  // `patientType` field, so we can filter against the current patient below.
  const allRelevantTemplates: (
    | IActivityTemplateWithPatient
    | IBatchTemplate
  )[] = [...templates.activities.withPatient, ...templates.batches];

  if (import.meta.env.DEV) {
    console.info(
      `[templates] Patient patientType=${patient.patientType}, ${allRelevantTemplates.length} parsed template(s) to filter`,
    );
  }

  const matchingTemplates = allRelevantTemplates.filter((template) => {
    const isMatch = template.template.patientType === patient.patientType;
    if (!isMatch && import.meta.env.DEV) {
      console.warn(
        `[templates] Filtered out "${template.template.title}" (${
          isBatchTemplate(template) ? "batch" : "activity"
        }) — patientType ${template.template.patientType} ≠ ${patient.patientType}`,
      );
    }
    return isMatch;
  });

  if (matchingTemplates.length === 0) {
    return (
      <Text element="p" color="faded">
        <Trans>Inga mallar tillgängliga för denna patient</Trans>
      </Text>
    );
  }

  const { groups, ungroupedTemplates } = groupTemplatesByTag(
    matchingTemplates,
    groupTags,
  );

  const nonEmptyGroups = groups.filter((group) => group.templates.length > 0);

  return (
    <div className={styles.templatesContainer}>
      {nonEmptyGroups.map((group) => (
        <section key={group.groupId} className={styles.groupSection}>
          <Heading level="h3" weight="medium">
            {group.groupName}
          </Heading>
          <TemplateGroup
            templates={group.templates}
            onSuccess={onSuccess}
            currentEmployeeCompetence={currentEmployeeCompetence}
          />
        </section>
      ))}
      {ungroupedTemplates.length > 0 ? (
        <TemplateGroup
          templates={ungroupedTemplates}
          onSuccess={onSuccess}
          currentEmployeeCompetence={currentEmployeeCompetence}
        />
      ) : null}
    </div>
  );
};
